/**
 * Which way a screen transition should slide, given the previous and next
 * primary tab.
 *
 * Pulled out as a pure function (no framer-motion, no DOM) so the direction
 * logic is testable without mounting `ScreenSlot` — and so `ScreenSlot` itself
 * stays a thin "compute direction, hand it to AnimatePresence" component.
 *
 * The rule mirrors a real tab bar: tabs have a fixed left-to-right order
 * (`TAB_ORDER`). Moving to a tab further RIGHT in that order means the new
 * screen enters FROM the right (the content "slides in" from where that tab
 * physically sits); moving LEFT enters from the left. Anything involving a
 * screen with no tab (a classic SPA page under `<Outlet/>`, or the initial
 * mount) has no meaningful spatial relationship, so it fades in place instead
 * of picking an arbitrary direction.
 */

export type TabKey = "home" | "records" | "stats";

/** Left-to-right order of the bottom bar's navigable tabs. Must match
 * `PRIMARY_TABS` in `nav.ts` — kept as a separate literal here so this module
 * has zero dependency on the registry/RBAC wiring `nav.ts` carries. */
export const TAB_ORDER: readonly TabKey[] = ["home", "records", "stats"];

/** `1` = new content enters from the right (exiting to the left). `-1` = new
 * content enters from the left. `0` = no directional slide — fade only. */
export type SlideDirection = 1 | -1 | 0;

export function screenDirection(
  previous: TabKey | null,
  next: TabKey | null,
): SlideDirection {
  if (previous === next) return 0;

  const prevIndex = previous ? TAB_ORDER.indexOf(previous) : -1;
  const nextIndex = next ? TAB_ORDER.indexOf(next) : -1;

  // Either side isn't a recognised primary tab (a classic page, or the very
  // first render) — no spatial relationship to animate along, so fade only.
  if (prevIndex === -1 || nextIndex === -1) return 0;

  return nextIndex > prevIndex ? 1 : -1;
}
